import { useId, type ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'

import type { DetailUnavailableReason } from '@/api/competitionDetail'
import type { Envelope } from '@/api/types'
import { EmptyState, LoadingBlock } from '@/design'

import { competitionDetailErrorMessage } from './errors'
import { describeDetailReason } from './reasons'

/* What every tab is made of: a tab's read and its states, figure tiles, and a group of them. */

/**
 * A tab's read: a skeleton while it loads, the outage (in the error's own words) when it
 * fails, else its content. While new filters load, the previous figures stay, marked busy.
 */
export function TabRead<T>({
  read,
  loading,
  children,
}: {
  read: UseQueryResult<Envelope<T>>
  /** The skeleton's accessible name: "Loading the funnel". */
  loading: string
  children: (data: T) => ReactNode
}) {
  if (read.isPending) return <LoadingBlock rows={5} label={loading} />
  if (!read.data) {
    return <EmptyState variant="outage" compact announce description={competitionDetailErrorMessage(read.error)} />
  }
  return <div aria-busy={read.isFetching || undefined}>{children(read.data.data)}</div>
}

/**
 * One figure: its label, its value, and a line under it. A figure the API could not give
 * reads its reason's label ("Not live yet") in place of a value, never a 0, and why
 * under it — or `note`, a shorter line, where its group already says why in full.
 */
export function Tile({
  label,
  value,
  note,
  reason,
}: {
  label: string
  value?: ReactNode
  note?: ReactNode
  reason?: DetailUnavailableReason | null
}) {
  if (reason) {
    const { label: missing, why } = describeDetailReason(reason)
    return (
      <li className="ci-kpi cd-tile" data-unavailable={reason} title={note ? why : undefined}>
        <span className="ci-kpi__label">{label}</span>
        <span className="ci-kpi__value cd-tile__missing">{missing}</span>
        <span className="cd-tile__note">{note ?? why}</span>
      </li>
    )
  }
  return (
    <li className="ci-kpi cd-tile">
      <span className="ci-kpi__label">{label}</span>
      <span className="ci-kpi__value">{value}</span>
      {note ? <span className="cd-tile__note">{note}</span> : null}
    </li>
  )
}

/**
 * A part of a tab under its own heading: an h2 (the page's name is the h1; the tabs
 * themselves are not headings), which also names the section, so it is said once.
 */
export function Group({ title, className = 'cd-group', children }: { title: string; className?: string; children: ReactNode }) {
  const id = useId()
  return (
    <section className={className} aria-labelledby={id}>
      <h2 id={id} className="cd-group__title">
        {title}
      </h2>
      {children}
    </section>
  )
}

/** A titled group of tiles, with a sentence under its heading when it needs one. */
export function TileGroup({ title, lead, children }: { title: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <Group title={title}>
      {lead ? <p className="cd-note">{lead}</p> : null}
      <ul className="cd-tiles">{children}</ul>
    </Group>
  )
}
